/**
 * DiffHistory — admin-side panel listing every modification made on an entity.
 *
 * Usage: <DiffHistory entityType="company" entityId={c.id} />
 * Fetches `/api/admin/activity/entity/{type}/{id}`.
 */
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { History, User } from "lucide-react";

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch { return s; }
}

function fmtValue(v) {
  if (v === null || v === undefined || v === "") return <em className="text-neutral-400">vide</em>;
  if (typeof v === "boolean") return v ? "✓ oui" : "✗ non";
  if (typeof v === "object") return JSON.stringify(v).slice(0, 120);
  const s = String(v);
  return s.length > 120 ? s.slice(0, 120) + "…" : s;
}

export default function DiffHistory({ entityType, entityId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!entityId) return;
    api.get(`/admin/activity/entity/${entityType}/${entityId}`)
      .then((r) => setItems(r.data?.items || []))
      .catch((e) => toast.error(formatApiErrorDetail(e) || "Erreur"))
      .finally(() => setLoading(false));
  }, [entityType, entityId]);

  if (loading) {
    return <div className="rounded-xl bg-neutral-50 p-3 text-center text-xs text-neutral-500">Chargement…</div>;
  }
  if (!items.length) {
    return (
      <div className="rounded-xl border border-dashed border-neutral-200 bg-neutral-50 p-4 text-center text-xs text-neutral-500" data-testid="diff-history-empty">
        <History className="mx-auto h-4 w-4 text-neutral-300" />
        <div className="mt-1">Aucune modification enregistrée pour le moment.</div>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="diff-history">
      {items.map((it) => {
        const fields = Object.keys(it.diff || {});
        return (
          <div key={it.id} className="rounded-xl border border-neutral-100 bg-white p-3" data-testid={`diff-entry-${it.id}`}>
            <div className="flex flex-wrap items-center gap-2 text-[10px]">
              <User className="h-3 w-3 text-wine-600" />
              <span className="font-semibold text-neutral-700">{it.actor_email || "—"}</span>
              {it.actor_role && <span className="rounded-full bg-neutral-100 px-1.5 text-neutral-600">{it.actor_role}</span>}
              <span className="ml-auto text-neutral-400">{fmtDate(it.created_at)}</span>
            </div>
            <table className="mt-2 w-full text-[11px]">
              <tbody>
                {fields.map((k) => (
                  <tr key={k} className="border-t border-neutral-50">
                    <td className="py-1.5 pr-2 font-semibold text-neutral-600">{k}</td>
                    <td className="py-1.5 pr-2 text-rose-600 line-through">{fmtValue(it.diff[k].from)}</td>
                    <td className="py-1.5 text-emerald-700">{fmtValue(it.diff[k].to)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
